'use client';

import { useEffect, useState } from 'react';

interface EpicMomentEffectProps {
  effect: 'burst' | 'cascade' | 'ripple' | 'fireworks' | 'lightning-strike';
  trigger: 'auto' | 'on-page-load';
}

export default function EpicMomentEffect({ effect, trigger }: EpicMomentEffectProps) {
  const [isActive, setIsActive] = useState(false);
  const [particles, setParticles] = useState<Array<{id: number, x: number, y: number, delay: number, angle: number}>>([]);

  useEffect(() => {
    if (trigger === 'auto' || trigger === 'on-page-load') {
      // Trigger effect after page loads
      const timer = setTimeout(() => setIsActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [trigger]);

  useEffect(() => {
    if (!isActive) return;

    // Generate particles for burst/fireworks
    if (effect === 'burst' || effect === 'fireworks') {
      const count = effect === 'fireworks' ? 30 : 20;
      const newParticles = Array.from({ length: count }, (_, i) => ({
        id: i,
        x: 50,
        y: 50,
        delay: Math.random() * 0.3,
        angle: (360 / count) * i,
      }));
      setParticles(newParticles);
    }

    // Reset after animation completes
    const resetTimer = setTimeout(() => setIsActive(false), 2000);
    return () => clearTimeout(resetTimer);
  }, [isActive, effect]);

  if (!isActive) return null;

  // Burst effect
  if (effect === 'burst') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'hidden' }}>
        {particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '10px',
              height: '10px',
              background: 'radial-gradient(circle, #FFD700, #FFA500)',
              borderRadius: '50%',
              animation: `burst 1.5s ease-out forwards`,
              transform: `rotate(${p.angle}deg)`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100px',
            height: '100px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.8), transparent)',
            borderRadius: '50%',
            animation: 'flashExpand 1s ease-out',
          }}
        />
        <style jsx>{`
          @keyframes burst {
            to { transform: rotate(${0}deg) translateY(-300px); opacity: 0; }
          }
          @keyframes flashExpand {
            to { transform: translate(-50%, -50%) scale(10); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // Cascade effect
  if (effect === 'cascade') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'hidden' }}>
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${5 + i * 6}%`,
              top: '-10%',
              fontSize: '2rem',
              animation: `cascade 2s ease-out forwards`,
              animationDelay: `${i * 0.1}s`,
            }}
          >
            ✨
          </div>
        ))}
        <style jsx>{`
          @keyframes cascade {
            to { transform: translateY(120vh); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // Ripple effect
  if (effect === 'ripple') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: '50px',
              height: '50px',
              border: '3px solid rgba(102, 126, 234, 0.6)',
              borderRadius: '50%',
              animation: `ripple 2s ease-out forwards`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
        <style jsx>{`
          @keyframes ripple {
            to { transform: scale(15); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // Lightning strike
  if (effect === 'lightning-strike') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70 }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.9)',
            animation: 'flash 0.5s ease-out',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: '4px',
            height: '100%',
            background: 'linear-gradient(to bottom, #FFD700, #FFA500, transparent)',
            boxShadow: '0 0 20px #FFD700',
            animation: 'strike 0.3s ease-out',
          }}
        />
        <style jsx>{`
          @keyframes flash {
            0%, 100% { opacity: 0; }
            50% { opacity: 1; }
          }
          @keyframes strike {
            from { transform: translateX(-50%) scaleY(0); }
            to { transform: translateX(-50%) scaleY(1); }
          }
        `}</style>
      </div>
    );
  }

  // Fireworks
  if (effect === 'fireworks') {
    return (
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 70, overflow: 'hidden' }}>
        {particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${30 + Math.random() * 40}%`,
              top: `${20 + Math.random() * 30}%`,
              fontSize: '1.5rem',
              animation: `firework 1.5s ease-out forwards`,
              animationDelay: `${p.delay}s`,
            }}
          >
            🎆
          </div>
        ))}
        <style jsx>{`
          @keyframes firework {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.5); opacity: 1; }
            100% { transform: scale(0.5) translateY(100px); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  return null;
}

